import type { InvoiceDetailResponseDto } from '@trellis/api'
import { RichTextRenderer } from '@trellis/roots/data'
import { Avatar } from '@trellis/roots/media'
import { Typography } from '@trellis/roots/typography'
import { isRichTextEmpty } from '@trellis/shared'

import { ClientPreviewTrigger } from '@/components/client-preview-sheet'
import { formatCalendarDate } from '@/lib/format'
import { cn } from '@/lib/utils'

import { LineItemDriftIndicator } from './LineItemDriftIndicator'

type OrgSnapshot = InvoiceDetailResponseDto['orgSnapshot']

export interface InvoiceDocumentSectionProps {
  invoice: InvoiceDetailResponseDto
  org: OrgSnapshot
  currFmt: Intl.NumberFormat
  isOverdue: boolean
  onPreviewClient: (clientId: string) => void
}

function MetaCell({
  label,
  value,
  highlight = false,
}: {
  label: string
  value: string
  highlight?: boolean
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 @md:block">
      <Typography variant="overline" color="muted">
        {label}
      </Typography>
      <p
        className={cn(
          'text-[14.5px] font-medium tabular-nums @md:mt-1',
          highlight ? 'text-destructive-text' : 'text-foreground',
        )}
      >
        {value}
      </p>
    </div>
  )
}

function TotalRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-muted-foreground flex justify-between py-1.5 text-[13.5px]">
      <span>{label}</span>
      <span className="text-foreground tabular-nums">{value}</span>
    </div>
  )
}

export function InvoiceDocumentSection({
  invoice,
  org,
  currFmt,
  isOverdue,
  onPreviewClient,
}: InvoiceDocumentSectionProps) {
  const client = invoice.clientSnapshot
  const taxPct = Math.round(invoice.taxRate * 100)

  return (
    <div className="overflow-hidden rounded-xl border">
      {/* From / Billed to */}
      <div className="border-b p-7">
        <div className="grid gap-8 @2xl:grid-cols-2">
          {/* From */}
          <div>
            <Typography variant="overline" color="muted" className="mb-2.5">
              From
            </Typography>
            {org ? (
              <>
                <div className="flex items-center gap-2.5">
                  <Avatar name={org.name ?? '?'} size="sm" shape="square" />
                  <div>
                    <Typography variant="h5">{org.name}</Typography>
                    {org.taxIdValue && (
                      <Typography variant="body-sm" color="muted">
                        {org.taxIdType === 'Other' ? org.taxIdCustomLabel : org.taxIdType}{' '}
                        {org.taxIdValue}
                      </Typography>
                    )}
                  </div>
                </div>
                <div className="text-muted-foreground mt-3 space-y-0.5 text-[13px] leading-[1.55]">
                  {org.address &&
                    org.address
                      .split('\n')
                      .filter(Boolean)
                      .map((line, lineIdx) => <p key={lineIdx}>{line}</p>)}
                  {org.email && <p>{org.email}</p>}
                  {org.phone && <p>{org.phone}</p>}
                </div>
              </>
            ) : (
              <Typography variant="body-sm" color="muted">
                —
              </Typography>
            )}
          </div>

          {/* Billed to */}
          <div>
            <Typography variant="overline" color="muted" className="mb-2.5">
              Billed to
            </Typography>
            {client ? (
              <>
                <div className="flex items-center gap-2.5">
                  <Avatar name={client.name} size="sm" />
                  <div>
                    <Typography variant="h5">{client.name}</Typography>
                    {client.company && (
                      <Typography variant="body-sm" color="muted">
                        {client.company}
                      </Typography>
                    )}
                  </div>
                </div>
                <div className="text-muted-foreground mt-3 space-y-0.5 text-[13px] leading-[1.55]">
                  {client.address &&
                    client.address
                      .split('\n')
                      .filter(Boolean)
                      .map((line, lineIdx) => <p key={lineIdx}>{line}</p>)}
                  {client.email && <p>{client.email}</p>}
                  {client.phone && <p>{client.phone}</p>}
                </div>
                <ClientPreviewTrigger
                  clientId={invoice.clientId}
                  onPreview={onPreviewClient}
                  className="mt-2.5"
                />
              </>
            ) : (
              <Typography variant="body-sm" color="muted">
                {invoice.clientName}
              </Typography>
            )}
          </div>
        </div>

        {/* Meta strip */}
        <div className="mt-6 grid gap-2 border-t pt-5 @md:grid-cols-3 @md:gap-4">
          <MetaCell label="Invoice ID" value={invoice.invoiceNumber} />
          <MetaCell label="Issued" value={formatCalendarDate(invoice.issueDate)} />
          <MetaCell
            label="Due"
            value={invoice.dueAt ? formatCalendarDate(invoice.dueAt) : '—'}
            highlight={isOverdue}
          />
        </div>
      </div>

      {/* Line items */}
      <div className="p-7">
        <Typography variant="overline" color="muted" className="mb-3">
          Line items
        </Typography>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[420px] border-collapse text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-muted-foreground pb-2 text-left text-[11.5px] font-medium tracking-[0.06em] uppercase">
                  Description
                </th>
                <th className="text-muted-foreground hidden w-20 pb-2 text-right text-[11.5px] font-medium tracking-[0.06em] uppercase sm:table-cell">
                  Qty
                </th>
                <th className="text-muted-foreground hidden w-28 pb-2 text-right text-[11.5px] font-medium tracking-[0.06em] uppercase sm:table-cell">
                  Unit price
                </th>
                <th className="text-muted-foreground hidden w-28 pb-2 text-right text-[11.5px] font-medium tracking-[0.06em] uppercase sm:table-cell">
                  Discount
                </th>
                <th className="text-muted-foreground w-24 pb-2 text-right text-[11.5px] font-medium tracking-[0.06em] uppercase sm:w-28">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {invoice.lineItems.map((lineItem) => {
                const hasDiscount =
                  lineItem.discountType !== 'NONE' &&
                  !isNaN(lineItem.discount) &&
                  lineItem.discount > 0
                const discountLabel = hasDiscount
                  ? lineItem.discountType === 'PERCENTAGE'
                    ? `-${lineItem.discount}%`
                    : `-${currFmt.format(lineItem.discount)}`
                  : null

                return (
                  <tr key={lineItem.id} className="border-b last:border-0">
                    <td className="py-3.5">
                      <div className="flex items-center gap-1.5 font-medium">
                        {lineItem.title}
                        <LineItemDriftIndicator changedFields={lineItem.changedFields} />
                      </div>
                      {lineItem.description && (
                        <RichTextRenderer
                          content={lineItem.description}
                          className="text-muted-foreground mt-0.5 text-xs"
                        />
                      )}
                      <div className="text-muted-foreground mt-1 text-xs tabular-nums sm:hidden">
                        {lineItem.quantity} × {currFmt.format(lineItem.unitPrice)}
                        {discountLabel && <> · {discountLabel}</>}
                      </div>
                    </td>
                    <td className="hidden py-3.5 text-right tabular-nums sm:table-cell">
                      {lineItem.quantity}
                    </td>
                    <td className="hidden py-3.5 text-right tabular-nums sm:table-cell">
                      {currFmt.format(lineItem.unitPrice)}
                    </td>
                    <td className="text-muted-foreground hidden py-3.5 text-right tabular-nums sm:table-cell">
                      {discountLabel ?? '—'}
                    </td>
                    <td className="py-3.5 text-right font-medium tabular-nums">
                      {currFmt.format(lineItem.subtotal)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="mt-4 flex justify-end">
          <div className="w-72">
            <TotalRow label="Subtotal" value={currFmt.format(invoice.subtotal)} />
            {invoice.discountType !== 'NONE' && invoice.discountAmount > 0 && (
              <TotalRow
                label={
                  invoice.discountType === 'PERCENTAGE'
                    ? `Discount (${invoice.discount}%)`
                    : 'Discount'
                }
                value={`-${currFmt.format(invoice.discountAmount)}`}
              />
            )}
            {invoice.taxType !== 'NONE' && (
              <TotalRow
                label={`Tax (${taxPct}%)`}
                value={currFmt.format(invoice.taxAmount)}
              />
            )}
            <div className="flex items-baseline justify-between border-t py-3.5">
              <span className="text-sm font-medium">Total</span>
              <span
                className="tabular-nums"
                style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 900,
                  fontSize: 24,
                  letterSpacing: '-0.02em',
                }}
              >
                {currFmt.format(invoice.total)}
              </span>
            </div>
            <p className="text-muted-foreground text-right text-xs">
              {invoice.currency}
              {invoice.taxType !== 'NONE' ? ` · ${invoice.taxType} inclusive` : ''}
            </p>
          </div>
        </div>

        {/* Notes */}
        {!isRichTextEmpty(invoice.notes) && (
          <div className="bg-muted/40 mt-7 rounded-[10px] border p-4">
            <Typography variant="overline" color="muted" className="mb-2">
              Notes
            </Typography>
            <RichTextRenderer
              content={invoice.notes}
              className="text-[13.5px] leading-[1.55]"
            />
          </div>
        )}
      </div>
    </div>
  )
}
